// src/components/Interpolation/InterpolationPanel.tsx
import React, { useState, useEffect } from 'react';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { useProcessing } from '../../context/ProcessingContext';
import DataTabs from '../Grid/DataTabs';

interface ModelInfo {
  id: string;
  label: string;
  description: string;
  resolution: 64 | 128;
}

const ML_MODELS: ModelInfo[] = [
  // 64x Resolution Models
  {
    id: 'aigrid_1',
    label: 'AiGrid 1.0',
    description: 'Optimized base model for 64x resolution grids',
    resolution: 64
  },
  {
    id: 'aig_3po',
    label: 'AiG 3pO',
    description: 'Enhanced breakthrough model for 64x resolution',
    resolution: 64
  },
  // 128x Resolution Models
  {
    id: 'nn_v2',
    label: 'nn_v2',
    description: 'Advanced neural network for 128x resolution (v2)',
    resolution: 128
  },
  {
    id: 'nn_v2_1',
    label: 'nn_v2.1',
    description: 'Latest iteration of nn_v2 with improved accuracy',
    resolution: 128
  }
];

export const InterpolationPanel: React.FC = () => {
  const { state, dispatch } = useProcessing();
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedModel, setSelectedModel] = useState<string>('');

  // Determine current grid resolution
  const gridResolution = state.griddedData ? 
    Math.max(state.griddedData.dimensions.width, state.griddedData.dimensions.height) : null;

  // Filter models based on grid resolution
  const availableModels = ML_MODELS.filter(model => model.resolution === gridResolution);

  // Set default model when component mounts or grid resolution changes
  useEffect(() => {
    if (availableModels.length > 0 && !selectedModel) {
      setSelectedModel(availableModels[0].id);
    }
  }, [gridResolution, availableModels, selectedModel]);

  if (!state.griddedData) {
    return (
      <div className="panel-section">
        <p className="empty-state">Please complete the gridding step first.</p>
      </div>
    );
  }

  const processInterpolation = async () => {
    if (!state.griddedData || isProcessing || !selectedModel) return;
    
    setIsProcessing(true);
    dispatch({ type: 'SET_ERROR', payload: null });

    try {
      const response = await fetch('/api/interpolate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          grid: state.griddedData.grid,
          model: selectedModel,
          originalPoints: state.griddedData.metadata.original_points,
          gridState: {
            shape: [state.griddedData.dimensions.height, state.griddedData.dimensions.width],
            orientation: 'lower',
            coord_type: state.griddedData.metadata.coordinate_system,
            extent: state.rawData?.geographic_metadata.bounds_utm,
            dummy_mask: state.griddedData.grid.map(row => 
              row.map(val => val === -9999)
            )
          }
        })
      });

      if (!response.ok) {
        throw new Error(await response.text());
      }

      const result = await response.json();
      
      dispatch({
        type: 'SET_INTERPOLATED_DATA',
        payload: {
          ml: result
        }
      });

      dispatch({ type: 'SET_VIEW_STEP', payload: 'contour' });
      
    } catch (error) {
      dispatch({ 
        type: 'SET_ERROR', 
        payload: error instanceof Error ? error.message : 'Failed to process interpolation'
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="panel-section">
      <PanelGroup direction="horizontal">
        <Panel defaultSize={65} minSize={30}>
          <div className="grid-main-content">
            <div className="data-visualization">
              <img 
                src={`data:image/png;base64,${state.griddedData.plot}`}
                alt="Gridded data visualization"
                className="preview-image"
              />
            </div>

            <div className="grid-controls-container">
              <button
                className="grid-process-button"
                onClick={processInterpolation}
                disabled={isProcessing || !selectedModel}
              >
                {isProcessing ? (
                  <>
                    <span className="processing-spinner" />
                    <span>Processing...</span>
                  </>
                ) : (
                  'Run Interpolation'
                )}
              </button>
            </div>
          </div>
        </Panel>

        <PanelResizeHandle className="resize-handle" />

        <Panel defaultSize={35} minSize={20}>
          <div className="grid-sidebar">
            <div className="control-group">
              <h3>{gridResolution}x Resolution AI Models</h3>
              <div className="scheme-options">
                {availableModels.map((model) => (
                  <label key={model.id} className="scheme-option">
                    <input
                      type="radio"
                      name="modelSelection"
                      value={model.id}
                      checked={selectedModel === model.id}
                      onChange={(e) => setSelectedModel(e.target.value)}
                      disabled={isProcessing}
                    />
                    <div className="scheme-content">
                      <span className="scheme-label">{model.label}</span>
                      <span className="scheme-description">{model.description}</span>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div className="control-group">
              <h3>Grid Information</h3>
              <DataTabs 
                histogram={state.griddedData.histogram}
                stats={{
                  num_points: state.griddedData.stats.valid_cells,
                  z_min: state.griddedData.stats.min,
                  z_max: state.griddedData.stats.max,
                  z_mean: state.griddedData.stats.mean,
                  z_std: state.griddedData.stats.std
                }}
                geoMetadata={state.griddedData.metadata.geographic_metadata}
              />
            </div>

            <div className="stats-display">
              <h3>Grid Properties</h3>
              <table>
                <tbody>
                  <tr>
                    <th>Resolution</th>
                    <td>{state.griddedData.dimensions.width}×{state.griddedData.dimensions.height}</td>
                  </tr>
                  <tr>
                    <th>Coverage</th>
                    <td>{(state.griddedData.stats.coverage * 100).toFixed(1)}%</td>
                  </tr>
                  <tr>
                    <th>Cell Size</th>
                    <td>{state.griddedData.metadata.cell_size_meters.toFixed(2)}m</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </Panel>
      </PanelGroup>
    </div>
  );
};

export default InterpolationPanel;